import React, { useState, useContext, useEffect, useRef, useLayoutEffect } from "react";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Box,
  Tooltip,
  List,
  Portal,
} from "@material-ui/core";

// Modern Icons - Usando ícones mais modernos e minimalistas
import MenuOpenIcon from "@material-ui/icons/MenuOpen";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import CloseIcon from "@material-ui/icons/Close";
import AccountCircleOutlinedIcon from "@material-ui/icons/AccountCircleOutlined";
import RefreshOutlinedIcon from "@material-ui/icons/RefreshOutlined";
import NotificationsNoneOutlinedIcon from "@material-ui/icons/NotificationsNoneOutlined";
import PowerSettingsNewOutlinedIcon from "@material-ui/icons/PowerSettingsNewOutlined";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import Brightness4OutlinedIcon from '@material-ui/icons/Brightness4Outlined';
import Brightness7OutlinedIcon from '@material-ui/icons/Brightness7Outlined';
import SyncOutlinedIcon from "@material-ui/icons/SyncOutlined";
import ExitToAppOutlinedIcon from "@material-ui/icons/ExitToAppOutlined";
import WbSunnyOutlinedIcon from '@material-ui/icons/WbSunnyOutlined';
import NightsStayOutlinedIcon from '@material-ui/icons/NightsStayOutlined';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';

import MainListItems from "./MainListItems";
import PhoneShortcut from "./PhoneShortcut";
import PwaInstallNotice from "../components/PwaInstallNotice";
import usePhoneNavigation from "../hooks/usePhoneNavigation";
import useTicketKeyboardViewport from "../hooks/useTicketKeyboardViewport";
import Fade from "@material-ui/core/Fade";
import "../styles/phone-browser.css";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import MaintenanceNotice from "../components/MaintenanceNotice";
import UserAvatar from "../components/UserAvatar";

import logo from "../assets/logo.png";
import darkLogo from "../assets/dark-logo.png";
import { SocketContext } from "../context/Socket/SocketContext";
import ChatPopover from "../pages/Chat/ChatPopover";
import ActivityNotificationsPopover from "../components/ActivityNotificationsPopover";
import MentionsPopOver from "../components/MentionsPopOver";
import { ReleaseVersionButton } from "../components/ReleaseNotesModal";
import { SupportIssuesButton } from "../components/SupportIssuesModal";
import releaseInfo from "../generated/releaseInfo.json";

import usePagePreloader from "../hooks/usePagePreloader";
import { useTabletNavigation } from "../hooks/useTabletPortrait";
import { isIphonePwa } from "../utils/ipadPwa";

import { ColorModeContext } from "../layout/themeContext";

const drawerWidth = 230;
const drawerCollapsedWidth = 48;
const railWidth = 56;
const railExpandedWidth = 234;
const railGap = 8;
const railBottomGap = 10;
const desktopBarHeight = 48;
const railOpenDelay = 90;
const railCloseDelay = 160;

// The shell switches with the drawer, not with page grids at the 960px breakpoint.
// Phone navigation also covers a phone's wider landscape viewport.
const compactNavigationStyles = (theme, styles) => ({
  [theme.breakpoints.down("xs")]: styles,
  "html[data-phone-navigation] &": styles,
});

// phone-browser.css owns the phone toolbar; keep legacy small-screen rules
// from overriding its title, action widths and in-flow drawer button.
const compactNavigationFallbackStyles = (theme, styles) => ({
  [theme.breakpoints.down("xs")]: {
    "html:not([data-phone-navigation]) &": styles,
  },
});

const navigationScrollbarStyles = (theme) => ({
  "&::-webkit-scrollbar": { width: 5 },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.16)" : "rgba(0, 0, 0, 0.2)",
    borderRadius: 3,
  },
  "@supports (-moz-appearance: none)": {
    scrollbarWidth: "thin",
    scrollbarColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.16) transparent" : "rgba(0, 0, 0, 0.2) transparent",
  },
});

const useStyles = makeStyles((theme) => ({
  statusbarSurface: {
    display: "none",
    ...compactNavigationStyles(theme, {
      display: "block",
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      // WebKit samples fixed edges from computed colors only above its 10px cutoff.
      // Keep this empty and paintless: it supplies the native statusbar color even
      // when iOS excludes the statusbar from the viewport (safe-area-top is 0).
      height: 11,
      backgroundColor: theme.mode === "dark" ? "#1c1c1c" : "#ffffff",
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      zIndex: 2147483647,
      pointerEvents: "none",
    }),
  },
  root: {
    display: "flex",
    height: "100vh",
    position: "relative",
    isolation: "isolate",
    "&[data-ticket-keyboard]": {
      position: "fixed !important",
      top: "var(--ticket-keyboard-top, 0px) !important",
      left: "0 !important",
      width: "100% !important",
      height: "var(--ticket-keyboard-height) !important",
      minHeight: "0 !important",
      maxHeight: "var(--ticket-keyboard-height) !important",
      overflow: "hidden !important",
    },
    "&[data-ticket-keyboard] [data-pwa-top-surface]": {
      position: "absolute !important",
    },
    "html[data-ipad-pwa] &": {
      "--ipad-appbar-row-height": "48px",
    },
    // iPad/tablet touch: dvh reflete o viewport visível real (exclui chrome do Safari
    // em browser mode e alinha com o que o standalone já faz). overflow:hidden impede
    // scroll no body causado por filhos que usem 100vh sem descontar a safe-area-top.
    // iPad/tablet: usa 100% em vez de 100dvh porque o #root tem zoom:0.9 aplicado
    // e viewport units (dvh/vh) não são afetados pelo zoom — ficariam menores que o container.
    "@media (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
      height: "100%",
      overflow: "hidden",
    },
    ...compactNavigationStyles(theme, {
      "html[data-ipad-pwa] &": {
        "--ipad-appbar-row-height": "var(--mobile-appbar-height, 48px)",
      },
      "@media (display-mode: browser)": {
        // Safari clips fixed subtrees above its floating bottom controls.
        // A positioned document surface lets the content paint behind them.
        position: "relative",
        inset: 0,
        width: "100%",
        height: "var(--mobile-browser-page-height, 100%)",
        minHeight: "var(--mobile-browser-page-height, 100%)",
        overflow: "hidden",
        "&[data-mobile-document-scroll]": {
          display: "block",
          height: "auto",
          minHeight: "var(--mobile-document-min-height, 100vh)",
          paddingTop: "var(--mobile-content-top, 56px)",
          boxSizing: "border-box",
          overflow: "visible",
          backgroundColor: `var(--mobile-document-canvas, ${theme.palette.background.paper})`,
        },
      },
    }),
    backgroundColor: theme.palette.fancyBackground,
    '& .MuiButton-outlinedPrimary': {
      color: theme.mode === 'light' ? '#FFF' : '#FFF',
      backgroundColor: theme.mode === 'light' ? theme.palette.primary.main : '#1c1c1c',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: theme.mode === 'light' ? 'Primary' : '#FFF',
    },
    // Estilos globais de scrollbar melhorados
    '& ::-webkit-scrollbar': {
      width: '6px',
      height: '6px',
    },
    '& ::-webkit-scrollbar-track': {
      background: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
      borderRadius: '3px',
    },
    '& ::-webkit-scrollbar-thumb': {
      background: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
      borderRadius: '3px',
      '&:hover': {
        background: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      },
    },
    '& ::-webkit-scrollbar-corner': {
      background: 'transparent',
    },
  },
  toolbar: {
    paddingRight: 24, // keep right padding
    paddingLeft: 24,
    minHeight: 48,
    position: "relative",
    zIndex: 1,
    pointerEvents: "auto",
    backgroundColor: "transparent",
    "html[data-ipad-pwa] &": {
      minHeight: "var(--ipad-appbar-row-height, 48px)",
    },
    transition: theme.transitions.create(["padding"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    ...compactNavigationStyles(theme, {
      minHeight: "var(--mobile-appbar-height)",
      paddingLeft: 8,
      paddingRight: 8,
    }),
  },
  appBar: {
    position: "fixed",
    top: 0,
    left: 0,
    width: `calc(100% - ${drawerCollapsedWidth}px)`,
    marginLeft: drawerCollapsedWidth,
    height: 48,
    // Reserve the same opaque top edge that WebKit samples for the statusbar.
    // This also applies with a trackpad, where pointer:coarse does not match.
    "html[data-ipad-pwa] &": {
      height: "calc(var(--ipad-appbar-row-height, 48px) + var(--ipad-statusbar-height))",
      paddingTop: "var(--ipad-statusbar-height)",
    },
    zIndex: theme.zIndex.drawer + 1, // Above drawer to mask shadow if needed, or tiles nicely
    pointerEvents: "auto",
    touchAction: "manipulation",
    transform: "translateZ(0)",
    WebkitTransform: "translateZ(0)",
    willChange: "transform",
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.mode === 'dark' ? "#1c1c1c" : "#ffffff",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: theme.mode === 'dark'
      ? "0 4px 10px -4px rgba(0, 0, 0, 0.3)"
      : "0 4px 10px -4px rgba(0, 0, 0, 0.1)",
    // The same line that outlines the floating menu.
    borderBottom: theme.neoUI?.effects?.contour?.main,
    "& .MuiIconButton-root": {
      color: theme.palette.primary.main + " !important",
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main + " !important",
    },
    "& svg": {
      color: theme.palette.primary.main + " !important",
    },
    ...compactNavigationStyles(theme, {
      width: "100%",
      marginLeft: 0,
      backdropFilter: "none",
      WebkitBackdropFilter: "none",
      height: "var(--mobile-appbar-total, 48px)",
      paddingTop: "var(--safe-area-top, 0px)",
      overflow: "hidden",
      "&::before": {
        content: "\"\"",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        pointerEvents: "none",
        zIndex: 0,
        backgroundColor: theme.mode === "dark" ? "#1c1c1c" : "#ffffff",
        height: "var(--safe-area-top)",
      },
    }),
    // iPad PWA portrait e landscape (≥600×600px): safe area para a AppBar não sobrepor a status bar
    "@media (display-mode: standalone) and (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
      height: "calc(48px + var(--safe-area-top))",
      paddingTop: "var(--safe-area-top)",
      overflow: "hidden",
      "&::before": {
        content: "\"\"",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        pointerEvents: "none",
        zIndex: 0,
        backgroundColor: theme.mode === "dark" ? "#1c1c1c" : "#ffffff",
        height: "var(--safe-area-top)",
      },
    },
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    ...compactNavigationStyles(theme, {
      marginLeft: 0,
      width: "100%",
    }),
  },
  appBarFull: {
    width: "100%",
    marginLeft: 0,
  },
  toolbarWithLogo: {
    paddingLeft: 13,
  },
  barLogo: {
    display: "block",
    height: 36,
    width: "auto",
    flexShrink: 0,
    marginRight: theme.spacing(2),
  },
  // The desktop menu floats too: its width in the page never changes, and it grows over the content.
  // Top and bottom match the tickets box, so the two read as one row of panels.
  railSpacer: {
    flex: "none",
    width: railGap + railWidth,
  },
  rail: {
    top: desktopBarHeight + railGap,
    bottom: railBottomGap,
    left: railGap,
    height: "auto",
    width: railWidth,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    whiteSpace: "nowrap",
    borderRadius: 12,
    border: theme.neoUI?.effects?.contour?.main,
    boxShadow: theme.neoUI?.effects?.glow?.main,
    backgroundColor: theme.mode === 'dark' ? '#1c1c1c' : '#fff',
    transition: "width 180ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 180ms ease-out",
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  },
  railOpen: {
    width: railExpandedWidth,
    boxShadow: theme.mode === 'dark' ? "0 18px 50px rgba(0, 0, 0, 0.35)" : "0 18px 50px rgba(0, 0, 0, 0.16)",
  },
  railContent: {
    "&&": {
      height: "auto",
      flex: "1 1 auto",
      minHeight: 0,
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
  },
  railList: {
    "&&": {
      flex: "1 0 auto",
      display: "flex",
      flexDirection: "column",
      // With the item's own 2px and the 1px edge, the first item sits 7px from the top, as from
      // the sides, and the last one 7px from the line below it.
      padding: "4px 0 5px",
      overflow: "visible",
      "& .MuiListItem-root": {
        height: 40,
        padding: "0 9px",
        "@media (max-height: 800px)": { height: 36 },
      },
      "& .MuiListItemIcon-root": {
        minWidth: 24,
        width: 24,
        "& svg": { fontSize: 24 },
      },
      "& .MuiListItemText-primary": { fontSize: 15 },
      // Each item is a slot that grows with the window, so the gaps stay equal from top to bottom.
      "& > div": { flex: "1 0 auto", display: "flex", flexDirection: "column" },
      "& > div > li": { flex: "1 0 auto", maxHeight: 68 },
      "& > div > li:last-child": { flex: "none" },
      "& > div > [aria-hidden]": { display: "none" },
    },
  },
  railFooter: {
    "&&": {
      flex: "none",
      // The same height closed and open, so the menu only grows sideways.
      height: 44,
      boxSizing: "border-box",
      overflow: "hidden",
      paddingTop: 0,
      paddingBottom: 0,
      backgroundColor: "transparent",
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  menuButton: {
    ...theme.neoUI?.buttons?.action?.root,
    position: "fixed",
    top: 17,
    "html[data-ipad-pwa] &": {
      top: "calc(var(--ipad-statusbar-height) + var(--ipad-appbar-row-height, 48px) / 2 - 15px)",
    },
    // Collapsed, the button takes the logo's place, centered on the rail.
    left: ({ drawerOpen }) => drawerOpen
      ? drawerWidth - 38
      : (drawerCollapsedWidth - 30) / 2,
    zIndex: theme.zIndex.drawer + 2,
    pointerEvents: "auto",
    touchAction: "manipulation",
    transform: "translateZ(0)",
    WebkitTransform: "translateZ(0)",
    willChange: "transform",
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    width: 30,
    height: 30,
    minHeight: 30,
    padding: 0,
    borderRadius: "50%",
    transition: theme.transitions.create(['left', 'background-color', 'box-shadow'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    "@media (display-mode: standalone) and (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
      top: "calc(var(--safe-area-top) + 13px)",
    },
    ...compactNavigationFallbackStyles(theme, {
      left: ({ drawerOpen }) => drawerOpen ? drawerWidth - 15 : 13,
      top: "calc(var(--safe-area-top) + 13px)",
      zIndex: theme.zIndex.modal + 2,
      "@media (display-mode: browser)": {
        left: ({ drawerOpen }) => drawerOpen ? drawerWidth + 20 : 13,
        zIndex: 99999,
        boxShadow: theme.mode === "dark"
          ? "0 6px 18px rgba(0, 0, 0, 0.38)"
          : "0 6px 18px rgba(0, 0, 0, 0.18)",
      },
    }),
    "&:hover": {
      ...theme.neoUI?.buttons?.action?.root?.["&:hover"],
    },
    "& .MuiSvgIcon-root": {
      fontSize: 20,
      color: "#fff",
    },
    "body.kanban-card-description-modal-open &": {
      opacity: 0,
      visibility: "hidden",
      pointerEvents: "none",
    },
  },
  title: {
    flexGrow: 1,
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.primary.main,
    lineHeight: 1.2,
    ...compactNavigationFallbackStyles(theme, {
      display: "none",
    }),
  },
  toolbarActions: {
    display: "flex",
    alignItems: "center",
    ...compactNavigationFallbackStyles(theme, {
      width: "100%",
      paddingLeft: 42,
      justifyContent: "space-between",
    }),
  },
  drawerPaper: {
    position: "relative",
    height: "100%",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.mode === 'dark' ? '#1c1c1c' : '#fff',
    borderRight: "none",
    ...compactNavigationStyles(theme, {
      backdropFilter: "none",
      WebkitBackdropFilter: "none",
    }),
    // Sombra suavizada com deslocamento vertical positivo
    boxShadow: theme.mode === 'dark'
      ? "2px 10px 20px -8px rgba(0, 0, 0, 0.4)"
      : "2px 10px 20px -8px rgba(0, 0, 0, 0.15)",
    overflowX: "hidden",
    zIndex: theme.zIndex.drawer,
    display: "flex",
    flexDirection: "column",
    "&:hover $containerWithScroll::-webkit-scrollbar-thumb, &:hover $listWrapper::-webkit-scrollbar-thumb": {
      backgroundColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.36)" : "rgba(0, 0, 0, 0.34)",
    },
    "@supports (-moz-appearance: none)": {
      "&:hover $containerWithScroll, &:hover $listWrapper": {
        scrollbarColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.36) transparent" : "rgba(0, 0, 0, 0.34) transparent",
      },
    },
  },
  drawerInnerCloseButton: {
    ...theme.neoUI?.buttons?.action?.root,
    position: "absolute",
    top: "calc(var(--safe-area-top) + 13px)",
    right: 12,
    zIndex: 2,
    pointerEvents: "auto",
    touchAction: "manipulation",
    transform: "translateZ(0)",
    WebkitTransform: "translateZ(0)",
    width: 30,
    height: 30,
    minHeight: 30,
    padding: 0,
    borderRadius: "50%",
    "& .MuiSvgIcon-root": {
      fontSize: 20,
      color: "#fff",
    },
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: drawerCollapsedWidth,
    ...compactNavigationStyles(theme, {
      width: 0, // Hides completely on small screens when closed
    }),
    boxShadow: theme.mode === 'dark'
      ? "2px 10px 20px -8px rgba(0, 0, 0, 0.4)"
      : "2px 10px 20px -8px rgba(0, 0, 0, 0.15)",
  },
  appBarSpacer: {
    minHeight: 48,
  },
  content: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: "auto",
    marginTop: 48,
    "html[data-ipad-pwa] &": {
      marginTop: "calc(var(--ipad-appbar-row-height, 48px) + var(--ipad-statusbar-height))",
    },
    position: "relative",
    zIndex: 0,
    // iPad PWA portrait e landscape: conteúdo deve começar abaixo da AppBar + safe area
    "@media (display-mode: standalone) and (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
      marginTop: "calc(48px + var(--safe-area-top))",
    },
    ...compactNavigationStyles(theme, {
      marginTop: "var(--mobile-content-top, 48px)",
      backgroundColor: theme.palette.fancyBackground,
      "@media (display-mode: browser)": {
        position: "absolute",
        top: "var(--mobile-content-top, 48px)",
        right: 0,
        bottom: 0,
        left: 0,
        marginTop: 0,
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorY: "contain",
        zIndex: 0,
        "&[data-mobile-document-scroll]": {
          position: "relative",
          top: 0,
          bottom: "auto",
          height: "auto",
          minHeight: "calc(var(--mobile-document-min-height, 100vh) - var(--mobile-content-top, 56px))",
          overflow: "visible",
          backgroundColor: `var(--mobile-document-canvas, ${theme.palette.background.paper})`,
        },
      },
    }),
  },
  containerWithScroll: {
    flex: 1,
    padding: ({ drawerOpen }) => drawerOpen ? theme.spacing(1) : theme.spacing(1, 0),
    overflowY: "auto",
    overscrollBehavior: "none",
    ...navigationScrollbarStyles(theme),
    "& .MuiListItemText-primary": {
      fontSize: "14px",
      fontWeight: 500,
    },
    "& .MuiListItemIcon-root": {
      minWidth: ({ drawerOpen }) => drawerOpen ? 40 : 0,
    },
    "& .MuiListItem-root.Mui-selected": {
      backgroundColor: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
      "&:hover": {
        backgroundColor: theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      },
      "& .MuiListItemText-primary": {
        color: theme.mode === 'dark' ? '#fff' : theme.palette.text.primary,
        fontWeight: 600,
      },
      "& .MuiListItemIcon-root": {
        color: theme.mode === 'dark' ? '#fff' : theme.palette.primary.main,
      },
    },
  },
  logo: {
    height: 60, //tava 40
    maxWidth: 180, //tava 140
    objectFit: "contain",
    display: ({ drawerOpen }) => drawerOpen ? "block" : "none",
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "0 16px",
    minHeight: "64px",
    ...compactNavigationStyles(theme, {
      minHeight: "calc(var(--mobile-appbar-height) + var(--safe-area-top))",
      padding: "calc(var(--safe-area-top) + 8px) 16px 0 16px",
    }),
    // iPad PWA portrait e landscape: logo do drawer não deve ficar sob a status bar
    "@media (display-mode: standalone) and (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
      minHeight: "calc(64px + var(--safe-area-top))",
      padding: "calc(var(--safe-area-top) + 8px) 16px 0 16px",
    },
  },
  iconButtonLabel: {
    fontSize: 20, // Reverted to smaller size
    color: theme.palette.primary.main,
  },
  iconButton: {
    width: 34, // Compact fixed size
    height: 34,
    padding: 6, // Adjusted padding
    margin: theme.spacing(0, 0.4), // Slight margin
    borderRadius: "50%",
    color: theme.palette.primary.main,
    pointerEvents: "auto",
    touchAction: "manipulation",
    "&:hover": {
      backgroundColor: theme.mode === 'light'
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.12)",
      "& .MuiSvgIcon-root": {
        transform: "scale(1.1)",
      },
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
    },
  },
  toolbarAvatar: {
    width: 24, // Reverted size
    height: 24,
    "html[data-phone-navigation] &": {
      width: 30,
      height: 30,
      fontSize: "0.875rem",
    },
    backgroundColor: theme.palette.primary.main,
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#fff",
  },
  listWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "calc(100% - 64px)",
    overflowY: "auto",
    overflowX: "hidden",
    ...navigationScrollbarStyles(theme),
    ...compactNavigationStyles(theme, {
      height: "calc(100% - var(--mobile-appbar-total))",
    }),
    // iPad PWA portrait e landscape: compensa o toolbarIcon expandido pelo safe area
    "@media (display-mode: standalone) and (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
      height: "calc(100% - 64px - var(--safe-area-top))",
    },
  },
  versionInfo: {
    fontSize: 12,
    padding: theme.spacing(1, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    color: theme.mode === 'dark' ? 'rgba(255,255,255,0.5)' : theme.palette.text.secondary,
    backgroundColor: theme.mode === 'dark' ? '#1c1c1c' : '#fff',
    ...compactNavigationStyles(theme, {
      paddingBottom: "calc(8px + var(--mobile-browser-bottom-extension, 0px))",
    }),
  },
  versionInfoCollapsed: {
    justifyContent: "center",
    padding: theme.spacing(1, 0.75),
  },
  versionButton: {
    fontSize: 12,
    fontWeight: 600,
    color: "inherit",
    borderRadius: 4,
    padding: theme.spacing(0.25, 0.75),
    lineHeight: 1.4,
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
}));

const PhoneDrawerShortcuts = ({ themeMode, iconButtonClassName, onToggleTheme, onRefresh, operationalAccess }) => (
    <Box data-phone-drawer-utilities>
      <PhoneShortcut label="Tema">
        <IconButton className={iconButtonClassName} onClick={onToggleTheme}>
          {themeMode === "dark" ? <WbSunnyOutlinedIcon /> : <NightsStayOutlinedIcon />}
        </IconButton>
      </PhoneShortcut>
      <PhoneShortcut label="Atualizar">
        <IconButton className={iconButtonClassName} onClick={onRefresh}><SyncOutlinedIcon /></IconButton>
      </PhoneShortcut>
      {operationalAccess && <>
        <PhoneShortcut label="Central pessoal"><MentionsPopOver /></PhoneShortcut>
        <PhoneShortcut label={i18n.t("mainDrawer.appBar.activities.title")}><ActivityNotificationsPopover /></PhoneShortcut>
        <PhoneShortcut label="Conversas"><ChatPopover /></PhoneShortcut>
        <PhoneShortcut label="Avisos"><AnnouncementsPopover /></PhoneShortcut>
      </>}
    </Box>
);

const LoggedInLayout = ({ children, themeToggle }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileDrawerTop, setMobileDrawerTop] = useState(0);
  const classes = useStyles({ drawerOpen });
  const location = useLocation();
  const contentRef = useRef(null);
  const maintenanceAnchorRef = useRef(null);
  const keyboardViewportBaselineRef = useRef(0);

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, loadingScreen } = useContext(AuthContext);
  const { user, isAuth, billing, isCompanyRestricted, billingLoading } = useContext(AuthContext);
  const operationalAccess = Boolean(isAuth && user?.companyId && billing && !isCompanyRestricted && !billingLoading);

  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const isCoarsePointer = useMediaQuery("(pointer: coarse)");
  const isBrowserDisplayMode = useMediaQuery("(display-mode: browser)");
  const { isPhoneNavigation, isPhoneBrowser, isPhonePwa } = usePhoneNavigation();
  const isCompactNavigation = isPhoneNavigation || isNarrowScreen;
  const tabletNavigation = useTabletNavigation();
  const isTabletNavigation = tabletNavigation && !isCompactNavigation;
  // Floats over the page like the phone's menu, so opening it never changes the page's width.
  const usesOverlayMenu = isPhoneNavigation || isTabletNavigation;
  // On these the top bar stays solid above the menu, so it also closes it.
  const barStaysAboveMenu = isTabletNavigation || (isPhonePwa && isIphonePwa());
  const usesFloatingRail = !isCompactNavigation && !isTabletNavigation;
  const railTimer = useRef(null);
  const drawerVariant = isCompactNavigation || isTabletNavigation ? "temporary" : "permanent";
  useLayoutEffect(() => {
    document.documentElement.toggleAttribute("data-tablet-navigation", isTabletNavigation);
    return () => document.documentElement.removeAttribute("data-tablet-navigation");
  }, [isTabletNavigation]);
  const shellRef = useRef(null);
  useTicketKeyboardViewport(shellRef, isPhoneNavigation && !(loading && loadingScreen !== "splash") && /^\/tickets\/[^/]+/.test(location.pathname), isPhonePwa);
  const [isStandalonePwa, setIsStandalonePwa] = useState(false);

  useEffect(() => {
    if (usesOverlayMenu) setDrawerOpen(false);
  }, [location.pathname, location.key, usesOverlayMenu]);

  useEffect(() => {
    if (!isPhoneBrowser || !drawerOpen) return undefined;

    const viewport = window.visualViewport;
    let frameId;
    const followViewport = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const visualOffset = viewport && viewport.scale === 1 ? viewport.offsetTop : 0;
        setMobileDrawerTop(window.scrollY + visualOffset);
      });
    };
    followViewport();
    window.addEventListener("scroll", followViewport, { passive: true });
    window.addEventListener("resize", followViewport);
    if (viewport) {
      viewport.addEventListener("scroll", followViewport);
      viewport.addEventListener("resize", followViewport);
    }
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", followViewport);
      window.removeEventListener("resize", followViewport);
      if (viewport) {
        viewport.removeEventListener("scroll", followViewport);
        viewport.removeEventListener("resize", followViewport);
      }
    };
  }, [isPhoneBrowser, drawerOpen]);

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);


  const socketManager = useContext(SocketContext);

  // Inicializa preloading das páginas críticas
  usePagePreloader();

  // The tablet menu opens below the top bar, which stays in use, so the page behind it isn't hidden from assistive tech.
  useEffect(() => {
    const appRoot = document.getElementById("root");
    if (appRoot && barStaysAboveMenu && drawerOpen) appRoot.removeAttribute("aria-hidden");
  }, [barStaysAboveMenu, drawerOpen]);

  useEffect(() => () => clearTimeout(railTimer.current), []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const updateStandaloneMode = () => {
      const isLegacyStandalone = typeof window.navigator !== "undefined" && window.navigator.standalone === true;
      setIsStandalonePwa(mediaQuery.matches || isLegacyStandalone);
    };

    updateStandaloneMode();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateStandaloneMode);
      return () => mediaQuery.removeEventListener("change", updateStandaloneMode);
    }

    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(updateStandaloneMode);
      return () => mediaQuery.removeListener(updateStandaloneMode);
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const applyKeyboardGap = (keyboardOpen) => {
      document.documentElement.style.setProperty(
        "--mobile-input-gap-main",
        keyboardOpen ? "0px" : "max(4px, calc(var(--safe-area-bottom) - 16px))"
      );
      document.documentElement.style.setProperty(
        "--mobile-input-gap-preview",
        keyboardOpen ? "0px" : "max(8px, calc(8px + var(--safe-area-bottom) - 16px))"
      );
    };

    const resetKeyboardTracking = () => {
      keyboardViewportBaselineRef.current = 0;
      applyKeyboardGap(false);
    };

    if (!isCompactNavigation || !isStandalonePwa) {
      resetKeyboardTracking();
      return undefined;
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      resetKeyboardTracking();
      return undefined;
    }

    const syncKeyboardState = () => {
      const viewportHeight = viewport.height || 0;
      if (!viewportHeight || Number.isNaN(viewportHeight)) {
        return;
      }

      if (viewportHeight > keyboardViewportBaselineRef.current) {
        keyboardViewportBaselineRef.current = viewportHeight;
      }

      const keyboardDelta = keyboardViewportBaselineRef.current - viewportHeight;
      const isKeyboardOpen = keyboardDelta > 100;
      applyKeyboardGap(isKeyboardOpen);
    };

    syncKeyboardState();

    viewport.addEventListener("resize", syncKeyboardState);
    viewport.addEventListener("scroll", syncKeyboardState);
    window.addEventListener("orientationchange", syncKeyboardState);
    window.addEventListener("resize", syncKeyboardState);

    return () => {
      viewport.removeEventListener("resize", syncKeyboardState);
      viewport.removeEventListener("scroll", syncKeyboardState);
      window.removeEventListener("orientationchange", syncKeyboardState);
      window.removeEventListener("resize", syncKeyboardState);
      resetKeyboardTracking();
    };
  }, [isCompactNavigation, isStandalonePwa]);

  useEffect(() => {
    if (isCompactNavigation || isTabletNavigation) {
      setDrawerOpen(false);
    }
  }, [isCompactNavigation, isTabletNavigation]);

  useEffect(() => {
    if (!isCompactNavigation || !contentRef.current) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [location.pathname, isCompactNavigation]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId) {
      return;
    }

    const socket = socketManager.getSocket(companyId);

    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
  }, [socketManager]);

  // Lists and the phone Kanban scroll with the document so Safari can paint
  // their content behind its floating controls. Conversations and editors stay bounded.
  const isTicketsListPage = /^\/tickets\/?$/.test(location.pathname);
  const isKanbanPage = /^\/kanban(?:\/|$)/i.test(location.pathname);
  const isViewportPage = /^\/tickets\/[^/]+/.test(location.pathname)
    || /^\/(?:chats|kanban|flowbuilder)(?:\/|$)/i.test(location.pathname);
  const useMobileDocumentScroll = isCompactNavigation && isCoarsePointer
    && isBrowserDisplayMode && !isStandalonePwa
    && (!isViewportPage || (isPhoneBrowser && isKanbanPage));
  const isMobilePwaMode = isCompactNavigation;
  const isMobileBrowserDrawerMode =
    isCompactNavigation && isBrowserDisplayMode;

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleDrawerToggle = () => {
    if (!drawerOpen) setMobileDrawerTop(useMobileDocumentScroll ? window.scrollY : 0);
    setDrawerOpen(!drawerOpen);

    // Quando recolher o drawer, fechar os submenus
    if (drawerOpen) {
      // Esta função será passada como ref para MainListItems
      if (window.closeSubmenus) {
        window.closeSubmenus();
      }
    }
  };

  const scheduleRail = (open, delay) => {
    clearTimeout(railTimer.current);
    railTimer.current = setTimeout(() => setDrawerOpen(open), delay);
  };

  // Expands with a mouse or the keyboard. A touch goes straight to the page it tapped.
  const railProps = usesFloatingRail ? {
    onPointerEnter: event => { if (event.pointerType !== "touch") scheduleRail(true, railOpenDelay); },
    onPointerLeave: event => { if (event.pointerType !== "touch") scheduleRail(false, railCloseDelay); },
    // React also delivers focus from dialogs opened inside the menu; those must not move it.
    onFocus: event => { if (event.currentTarget.contains(event.target) && event.target.matches(":focus-visible")) scheduleRail(true, 0); },
    onBlur: event => { if (event.currentTarget.contains(event.target) && !event.currentTarget.contains(event.relatedTarget)) scheduleRail(false, railCloseDelay); },
  } : {};

  const handleExpandDrawer = () => {
    if (!drawerOpen) {
      setMobileDrawerTop(useMobileDocumentScroll ? window.scrollY : 0);
      setDrawerOpen(true);
    }
  };

  const drawerClose = () => {
    if (isCompactNavigation || isTabletNavigation) {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  }

  const toggleColorMode = () => {
    colorMode.toggleColorMode();
  }



  if (loading && loadingScreen !== "splash") {
    return <BackdropLoading />;
  }

  return (
    <div ref={shellRef} className={classes.root} data-mobile-app-shell data-mobile-document-scroll={useMobileDocumentScroll ? "" : undefined}>
      <Portal><div className={classes.statusbarSurface} data-mobile-statusbar aria-hidden="true" /></Portal>
      <Drawer
        data-phone-drawer={usesOverlayMenu ? "" : undefined}
        PaperProps={usesFloatingRail ? railProps : usesOverlayMenu ? { "data-phone-drawer-panel": "", id: "phone-site-navigation", role: "dialog", "aria-modal": !barStaysAboveMenu, "aria-label": "Menu principal" } : undefined}
        TransitionComponent={isTabletNavigation ? Fade : undefined}
        transitionDuration={isTabletNavigation ? { enter: 240, exit: 200 } : undefined}
        variant={drawerVariant}
        onClose={handleDrawerToggle}
        ModalProps={isCompactNavigation ? {
          keepMounted: isPhoneNavigation,
          disableScrollLock: useMobileDocumentScroll,
          // Safari browser uses document space; installed phone PWAs keep a
          // viewport-fixed overlay with their own dynamic height and safe areas.
          style: {
            position: isPhonePwa ? "fixed" : "absolute",
            top: isPhonePwa ? 0 : mobileDrawerTop,
            width: "100%",
            height: isPhonePwa ? "var(--mobile-pwa-page-height, 100dvh)" : "var(--mobile-document-min-height, var(--mobile-browser-page-height, 100%))",
            backgroundColor: "transparent",
            boxShadow: "none",
          },
          disableEnforceFocus: barStaysAboveMenu,
        } : isTabletNavigation ? {
          keepMounted: true,
          // The top bar stays solid and usable above the menu.
          disableEnforceFocus: true,
          style: { position: "fixed", top: "var(--tablet-bar-height, 48px)", width: "100%", backgroundColor: "transparent", boxShadow: "none", zIndex: theme.zIndex.modal },
        } : undefined}
        BackdropProps={isCompactNavigation || isTabletNavigation ? { style: { position: usesOverlayMenu ? "fixed" : "absolute", touchAction: "none" } } : undefined}
        className={usesFloatingRail ? classes.railSpacer : isTabletNavigation ? undefined : drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: usesFloatingRail
            ? clsx(classes.rail, drawerOpen && classes.railOpen)
            : clsx(
              classes.drawerPaper,
              !drawerOpen && !isTabletNavigation && classes.drawerPaperClose
            ),
        }}
        open={drawerOpen}
      >
        {!barStaysAboveMenu && (usesOverlayMenu || isMobileBrowserDrawerMode) && drawerOpen && (
          <Tooltip title={usesOverlayMenu ? "Fechar menu" : "Recolher menu"}>
            <IconButton
              className={classes.drawerInnerCloseButton}
              data-phone-drawer-close={usesOverlayMenu ? "" : undefined}
              aria-label={usesOverlayMenu ? "Fechar menu" : "Recolher menu"}
              autoFocus={usesOverlayMenu || useMobileDocumentScroll}
              onClick={handleDrawerToggle}
            >
              {usesOverlayMenu ? <CloseIcon /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}

        {!barStaysAboveMenu && !usesFloatingRail && (
          <div className={classes.toolbarIcon} data-phone-drawer-header={usesOverlayMenu ? "" : undefined}>
            {/* On a phone this row only holds the close button. */}
            {!isPhoneNavigation && <img src={theme.mode === 'light' ? logo : darkLogo} className={classes.logo} alt={usesOverlayMenu ? "NeoZapp" : "logo"} />}
          </div>
        )}

        <div className={clsx(classes.listWrapper, usesFloatingRail && classes.railContent)} data-phone-drawer-content={usesOverlayMenu ? "" : undefined}>
          <List className={clsx(classes.containerWithScroll, usesFloatingRail && classes.railList)} data-phone-drawer-nav={usesOverlayMenu ? "" : undefined}>
            <MainListItems
              drawerClose={drawerClose}
              collapsed={!drawerOpen && !isTabletNavigation}
              onExpandDrawer={handleExpandDrawer}
              isMobilePwaMode={isMobilePwaMode}
            />
          </List>

          {isPhoneNavigation && (
            <PhoneDrawerShortcuts
              themeMode={theme.mode}
              iconButtonClassName={classes.iconButton}
              onToggleTheme={toggleColorMode}
              onRefresh={handleRefreshPage}
              operationalAccess={operationalAccess && user.id}
            />
          )}

          {user?.id ? (
            <Box className={clsx(classes.versionInfo, !drawerOpen && !isTabletNavigation && classes.versionInfoCollapsed, usesFloatingRail && classes.railFooter)} data-phone-drawer-footer={usesOverlayMenu ? "" : undefined}>
              <SupportIssuesButton
                user={user}
                socketManager={socketManager}
                collapsed={!drawerOpen && !isTabletNavigation}
              />
              <ReleaseVersionButton
                releaseInfo={releaseInfo}
                className={classes.versionButton}
                hidden={!drawerOpen && !isTabletNavigation}
                logo={usesOverlayMenu ? `${process.env.PUBLIC_URL}/android-chrome-192x192.png` : undefined}
              />
            </Box>
          ) : null}
        </div>
      </Drawer>

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      <AppBar
        data-pwa-top-surface
        position="fixed"
        className={clsx(classes.appBar, drawerOpen && !isTabletNavigation && !usesFloatingRail && classes.appBarShift, usesFloatingRail && classes.appBarFull)}
        elevation={0}
        onClickCapture={event => {
          // With the menu open, a tap on the bar closes it and still runs the action.
          if (barStaysAboveMenu && drawerOpen && !event.target.closest("[data-phone-menu-button]")) setDrawerOpen(false);
        }}
      >
        <Toolbar className={clsx(classes.toolbar, usesFloatingRail && classes.toolbarWithLogo)} data-phone-toolbar={usesOverlayMenu ? "" : undefined}>
          {usesFloatingRail && <img src={theme.mode === 'light' ? logo : darkLogo} className={classes.barLogo} alt="NeoZapp" />}
          {usesOverlayMenu && (
            <IconButton className={classes.menuButton} data-phone-menu-button aria-label={barStaysAboveMenu && drawerOpen ? "Fechar menu" : "Abrir menu"} aria-controls="phone-site-navigation" aria-expanded={drawerOpen} onClick={handleDrawerToggle}>
              {barStaysAboveMenu && drawerOpen ? <CloseIcon /> : <MenuOpenIcon />}
            </IconButton>
          )}
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            className={classes.title}
            data-phone-toolbar-title={usesOverlayMenu ? "" : undefined}
          >
            {usesOverlayMenu ? "NeoZapp" : greaterThenSm && (
              <>
                {i18n.t("mainDrawer.appBar.greeting.hello")} <b>{user.name}</b>, {i18n.t("mainDrawer.appBar.greeting.welcome")} <b>{user?.company?.name}</b>!
              </>
            )}
          </Typography>

          <Box className={classes.toolbarActions} data-phone-toolbar-actions={isPhoneNavigation ? "" : undefined}>
            {!isMobilePwaMode && !isPhoneNavigation && (
              <NotificationsVolume
                setVolume={setVolume}
                volume={volume}
              />
            )}

            {!isPhoneNavigation && (<>
            <Tooltip title={theme.mode === 'dark' ? i18n.t("mainDrawer.appBar.lightMode") : i18n.t("mainDrawer.appBar.darkMode")}>
              <IconButton
                className={classes.iconButton}
                onClick={toggleColorMode}
              >
                {theme.mode === 'dark' ?
                  <WbSunnyOutlinedIcon className={classes.iconButtonLabel} /> :
                  <NightsStayOutlinedIcon className={classes.iconButtonLabel} />
                }
              </IconButton>
            </Tooltip>

            <Tooltip title={i18n.t("mainDrawer.appBar.refresh")}>
              <IconButton
                className={classes.iconButton}
                onClick={handleRefreshPage}
              >
                <SyncOutlinedIcon className={classes.iconButtonLabel} />
              </IconButton>
            </Tooltip>

            {operationalAccess && user.id && <MentionsPopOver />}

            {operationalAccess && user.id && <ActivityNotificationsPopover />}


            </>)}
            {operationalAccess && user.id && (
              <NotificationsPopOver volume={volume} icon={isPhoneNavigation ? NotificationsNoneOutlinedIcon : undefined} />
            )}

            {!isPhoneNavigation && (<>
            {operationalAccess && <ChatPopover />}

            <Box component="span" display="inline-flex" ref={maintenanceAnchorRef}>
              {operationalAccess && <AnnouncementsPopover />}
            </Box>
            <MaintenanceNotice anchorRef={maintenanceAnchorRef} />

            </>)}
            {isPhoneNavigation && <Box component="span" display="inline-flex" ref={maintenanceAnchorRef} />}
            {isPhoneNavigation && <MaintenanceNotice anchorRef={maintenanceAnchorRef} />}

            <Tooltip title={i18n.t("mainDrawer.appBar.user.profile")}>
              <IconButton
                className={classes.iconButton}
                onClick={handleMenu}
              >
                <UserAvatar
                  user={user}
                  className={classes.toolbarAvatar}
                />
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                <PersonOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                <ExitToAppOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Botão flutuante do menu */}
      {!usesOverlayMenu && !usesFloatingRail && (!isMobileBrowserDrawerMode || !drawerOpen) ? (
        <Tooltip title={drawerOpen ? "Recolher menu" : "Expandir menu"}>
          <IconButton
            className={classes.menuButton}
            onClick={handleDrawerToggle}
          >
            {drawerOpen ? <ChevronLeftIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ) : null}

      <main ref={contentRef} className={classes.content} data-mobile-page-background={isTicketsListPage ? "tickets" : "default"} data-mobile-document-scroll={useMobileDocumentScroll ? "" : undefined}>
        {children ? children : null}
      </main>
      <PwaInstallNotice
        enabled={isPhoneBrowser}
        blocked={[drawerOpen, menuOpen, userModalOpen, loading, isViewportPage].some(Boolean)}
      />
    </div>
  );
};

export default LoggedInLayout;
